// Standalone display preference for the /words search results: list (default)
// or a larger mosaic card grid. This is a normal user preference, NOT an
// engineering toggle — it is persisted directly to localStorage under its own
// key rather than routed through feature-flags.ts's DEFS/dev-panel funnel.
import { useEffect, useState, useSyncExternalStore } from 'react';

export type ResultViewMode = 'list' | 'mosaic';

const STORAGE_KEY = 'words.view-mode';
/** Mosaic is desktop-only — matches the app's existing single-zone mobile breakpoint. */
const DESKTOP_QUERY = '(min-width: 768px)';

function safeLocalStorage(): Storage | null {
  try {
    return globalThis.localStorage ?? null;
  } catch {
    return null;
  }
}

function readMode(): ResultViewMode {
  return safeLocalStorage()?.getItem(STORAGE_KEY) === 'mosaic' ? 'mosaic' : 'list';
}

type Listener = () => void;
const listeners = new Set<Listener>();
function emit(): void {
  for (const listener of listeners) listener();
}
function subscribe(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function writeMode(mode: ResultViewMode): void {
  try {
    safeLocalStorage()?.setItem(STORAGE_KEY, mode);
  } catch {
    // localStorage may be unavailable (private mode, SSR) — degrade silently.
  }
  emit();
}

/** The stored list/mosaic preference, reactive within the current tab. */
export function useResultViewMode(): [ResultViewMode, (mode: ResultViewMode) => void] {
  const mode = useSyncExternalStore(subscribe, readMode, () => 'list' as ResultViewMode);
  return [mode, writeMode];
}

/** True at/above the 768px breakpoint where the mosaic toggle is available. */
export function useIsDesktopViewport(): boolean {
  const [isDesktop, setIsDesktop] = useState(
    () => typeof window !== 'undefined' && window.matchMedia(DESKTOP_QUERY).matches,
  );
  useEffect(() => {
    const mql = window.matchMedia(DESKTOP_QUERY);
    const onChange = () => setIsDesktop(mql.matches);
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, []);
  return isDesktop;
}
